import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { is } from 'typia'
import {
  MollieBanContactPayload,
  MollieBelfiusPayload, MollieBlikPayload,
  MollieCreditCardPayload,
  MollieEPSPayload,
  MollieIdealPayload,
  MollieKBCCBCPayload,
  MolliePayPalPayload,
  MolliePrzelewy24Payload,
  MollieTrustlyPayload,
  MollieBankTransferPayload,
  serializeDTO
} from '@cloudsuite/cs-payment-defs'
import type { KlarnaMethodId, In3MethodId, RivertyMethodId } from 'components/ordering/checkout/payment-methods/methods/method-ids.js'
import { MollieApplePayPayload } from '@cloudsuite/cs-payment-defs/dist/index.js'


export class StartMolliePaymentRequest {
  url: URL
  body: FormData
  method = 'POST' as const

  constructor(url: URL, startPaymentForm: StartMolliePaymentForm) {
    const formData: FormData = new FormData()
    const serializedPayload = is<StartMolliePaymentPayload>(startPaymentForm.startPaymentPayload) ? serializeDTO(startPaymentForm.startPaymentPayload) : JSON.stringify(startPaymentForm.startPaymentPayload)
    formData.append('start_payment_payload', serializedPayload)
    formData.append('psp_identifier', startPaymentForm.pspIdentifier)

    this.url = url
    this.body = formData
  }
}


export interface StartMolliePaymentForm {
  startPaymentPayload: StartMollieKlarnaPaymentPayload | StartMollieIn3PaymentPayload | StartMollieRivertyPaymentPayload | StartMolliePaymentPayload,
  pspIdentifier: 'mollie'
}

export type GenericStartMolliePaymentPayload = (
  MollieBanContactPayload
  | MollieBelfiusPayload
  | MollieEPSPayload
  | MollieKBCCBCPayload
  | MolliePayPalPayload
  | MollieIdealPayload
  | MolliePrzelewy24Payload
  | MollieTrustlyPayload
  | MollieBlikPayload
  | MollieBankTransferPayload
)

export type StartMolliePaymentPayload = (
  GenericStartMolliePaymentPayload
  | MollieCreditCardPayload
  | MollieApplePayPayload
)


export interface StartMollieKlarnaPaymentPayload {
  method: KlarnaMethodId,
}


export interface StartMollieIn3PaymentPayload {
  method: In3MethodId,
}


export interface StartMollieRivertyPaymentPayload {
  method: RivertyMethodId,
}


export class CouldNotCreatePaymentPayload {}


export class StartOnAccountPaymentRequest {
  url: URL
  method = 'POST' as const

  constructor(url: URL) {
    this.url = url
  }
}


export interface StartPaymentSuccessResponse {
  checkout_url: string
}


export interface StartPaymentErrorResponse {
  error: string
}


export const START_PAYMENT_API = Symbol('START_PAYMENT_API')


export interface StartPaymentApi {
  startMolliePayment(request: StartMolliePaymentRequest): Promise<unknown>
  startOnAccountPayment(request: StartOnAccountPaymentRequest): Promise<unknown>
}


@registerDependency(FAKE, START_PAYMENT_API)
export class FakeStartPaymentApi implements StartPaymentApi {
  checkoutUrl = new URL('/bla-bla', window.location.origin)
  requests: Array<StartMolliePaymentRequest | StartOnAccountPaymentRequest> = []

  private success: StartPaymentSuccessResponse = { checkout_url: this.checkoutUrl.toString() }
  private error: StartPaymentErrorResponse | null = null


  startMolliePayment = async (request: StartMolliePaymentRequest): Promise<unknown> => {
    if (this.error !== null) {
      return this.error
    }

    this.requests.push(request)
    return this.success
  }

  assertStartPaymentPayloadReceived(expected: StartMolliePaymentRequest): void {
    const filtered = this.requests.map(this.parsePaymentRequest)

    const expectedRequest = this.parsePaymentRequest(expected)
    const containsRequest = filtered.find((request: object) => JSON.stringify(request) === JSON.stringify(expectedRequest))

    if (containsRequest === undefined) {
      throw new Error(`Expected request ${JSON.stringify(expectedRequest, null, '\t')} not found in ${JSON.stringify(filtered, null, '\t')}`)
    }
  }

  private parsePaymentRequest(paymentRequest: StartMolliePaymentRequest | StartOnAccountPaymentRequest): object {
    if (!is<StartMolliePaymentRequest>(paymentRequest)) {
      return {}
    }
    const body: any = {}
    paymentRequest.body.forEach((value: FormDataEntryValue, key: string) => body[key] = value)
    return {
      url: paymentRequest.url,
      method: paymentRequest.method,
      body: body
    }
  }

  startOnAccountPayment = async (request: StartOnAccountPaymentRequest): Promise<unknown> => {
    if (this.error !== null) {
      return this.error
    }

    this.requests.push(request)
    return this.success
  }

  toThrowError(error: StartPaymentErrorResponse) {
    this.error = error
  }
}


@registerDependency(REAL, START_PAYMENT_API)
export class RealStartPaymentApi implements StartPaymentApi {
  startMolliePayment = async (request: StartMolliePaymentRequest): Promise<unknown>  => {
    const response = await fetch(request.url, { method: request.method, body: request.body })
    return response.json()
  }

  startOnAccountPayment = async (request: StartOnAccountPaymentRequest): Promise<unknown>  => {
    const response = await fetch(request.url, { method: request.method })
    return response.json()
  }
}
